/**
 * 应用入口 —— 装配 5 层（顺序有讲究）：
 *
 * ```
 * AppProvider        会话 / 主题 / 展开态（最外层：下面几层都要读会话）
 *   QueryClientProvider  服务端态缓存
 *     ThemeProvider        MUI 主题（由令牌构件）
 *       CssBaseline        归一化 + 背景/文字色
 *       RouterProvider     路由（未登录一律回 /login）
 * ```
 *
 * 🔴 **两处与后端联动的装配**：
 * 1. `configureApi` 注入"读会话"与"401 时清会话" —— **不能**在 `http.ts` 里直接 import store
 *    （那会是 `api → store → api` 的循环依赖，且把 HTTP 层绑死在 React 上）；
 * 2. 每请求的 `X-Ledger-Id` 由 `http.ts` 从会话里取 —— **端侧不缓存"当前账本"的任何派生结果**。
 *
 * 🆕 批次 `66`：`useRealtime()` 除了失效刷新，还把**实时提示条**喂给 store
 * （架构 §7.2「顶部轻提示条」）—— 投递口在 `api/realtime.ts`，渲染在 `shell/Shell.tsx`。
 * ⚠️ 它**不需要**额外的一层 Provider：提示条状态就在 `AppProvider` 里（见 `app/store.tsx`）。
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { AppProvider, useApp } from './app/store.js';
import { useThemeSync } from './app/useThemeSync.js';
import { configureApi } from './api/http.js';
// 🔴 L2 实时订阅（socket.io + 失效刷新）—— 必须在 QueryClientProvider **之内**（要拿 queryClient）
import { useRealtime } from './api/realtime.js';
import { AppRoutes } from './shell/AppRoutes.js';
import { buildTheme } from './theme/theme.js';

/** 主题 + 路由（读会话的组件必须放在 `AppProvider` 内） */
function Themed({ children }: { children: React.ReactNode }) {
  const { themeMode } = useApp();
  const theme = buildTheme(themeMode);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

/**
 * React Query：默认重试 1 次 · **窗口聚焦时重取** · 10 秒内不重复请求。
 *
 * 🔴 `refetchOnWindowFocus` 从 `false` 改为 `true`（批次 97 · `DR-057` D1.2）：
 * 原来"聚焦不重取"的理由是"账本应用没有实时性需求，省流量"，
 * 但它的实际后果是 —— **在另一个标签 / 手机端改完账，切回来看到的仍是旧余额**，
 * 而页面上没有任何"数据已过期"的提示（用户会以为记账没生效）。
 * 余额类接口很轻（一个账本几十个账户），一次聚焦重取的代价远小于"看到错数"。
 *
 * ⚠️ `staleTime` 保持 10 秒：它是**防抖**（防止连续聚焦刷屏），不是"允许过期"。
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: true, staleTime: 10_000 },
  },
});

function Root() {
  const { session, logout } = useApp();
  // 🔴 L2：会话在 ⇒ 连接 + 订阅当前账本；登出 ⇒ 断开（见 api/realtime.ts）
  useRealtime();
  // 🔴 主题（`AC-N3.2`）：登录后用服务端偏好覆盖本地缓存值（缓存只负责防闪，见 useThemeSync）
  useThemeSync();
  // 🔴 装配 HTTP 层与 React 状态的桥（见文件头说明 1）
  configureApi({
    getSession: () => session,
    onUnauthorized: () => logout(),
  });
  return (
    <Themed>
      <CssBaseline />
      <AppRoutes />
    </Themed>
  );
}

const container = document.getElementById('root');
if (container === null) throw new Error('找不到 #root 挂载点');

createRoot(container).render(
  <StrictMode>
    <AppProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Root />
        </BrowserRouter>
      </QueryClientProvider>
    </AppProvider>
  </StrictMode>,
);
